import { useEffect, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import {
  ChartLineUp, CurrencyDollar, Lightning, ListChecks,
  Sparkle, CircleNotch, Calendar, Cpu,
} from '@phosphor-icons/react';

const fmtUSD = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
const fmtUSD2 = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtTok = (n) => Number(n || 0).toLocaleString('es-GT');
const fmtGTQ = (usd) => `Q${(Number(usd || 0) * 7.7).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

const AiCostos = () => {
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState(null);
  const [recent, setRecent] = useState([]);
  const [byFeature, setByFeature] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    mode: 'days', days: 30, from: daysAgoIso(30), to: todayIso(),
  });

  const buildParams = () => {
    const p = new URLSearchParams();
    if (filters.mode === 'range') {
      p.set('from_date', filters.from);
      p.set('to_date', filters.to);
    } else {
      p.set('days', String(filters.days));
    }
    return p;
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const [s, d, r, f] = await Promise.all([
        api.get(`/ai-usage/summary?${params.toString()}`),
        api.get(`/ai-usage/daily?${params.toString()}`),
        api.get('/ai-usage/recent?limit=20'),
        api.get(`/ai-usage/by-feature?${params.toString()}`),
      ]);
      setSummary(s.data);
      setDaily(d.data);
      setRecent(r.data.calls || []);
      setByFeature(f.data.features || []);
    } catch (e) {
      toast.error('Error cargando costos de IA');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [filters.mode, filters.days, filters.from, filters.to]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto" style={{ background: 'var(--bg)', color: 'var(--text)' }} data-testid="ai-costos-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-faint)] font-bold flex items-center gap-1.5">
            <Cpu size={11} weight="fill" /> Monitor de consumo IA
          </p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight heading-font">Costos de IA</h1>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Claude Sonnet 4.5 vía Universal Key. Tokens estimados con tiktoken.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="flex gap-1 bg-[var(--surface)] border border-[var(--border)] rounded-md p-0.5">
            <button
              onClick={() => setFilters({ ...filters, mode: 'days' })}
              className={`px-2.5 py-1.5 text-[11px] font-bold rounded ${filters.mode === 'days' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-muted)] hover:text-[var(--text)]'}`}
            >Últimos</button>
            <button
              onClick={() => setFilters({ ...filters, mode: 'range' })}
              className={`px-2.5 py-1.5 text-[11px] font-bold rounded ${filters.mode === 'range' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-muted)] hover:text-[var(--text)]'}`}
            >Rango</button>
          </div>
          {filters.mode === 'days' ? (
            <select
              value={filters.days}
              onChange={(e) => setFilters({ ...filters, days: Number(e.target.value) })}
              className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-3 py-2 text-sm"
              data-testid="filter-days"
            >
              <option value={7}>7 días</option>
              <option value={15}>15 días</option>
              <option value={30}>30 días</option>
              <option value={60}>60 días</option>
              <option value={90}>90 días</option>
            </select>
          ) : (
            <div className="flex gap-1 items-center">
              <input type="date" value={filters.from} max={filters.to}
                onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-2 py-1.5 text-xs" />
              <span className="text-[var(--text-faint)] text-xs">→</span>
              <input type="date" value={filters.to} min={filters.from} max={todayIso()}
                onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-2 py-1.5 text-xs" />
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[var(--text-muted)]">
          <CircleNotch size={28} className="animate-spin inline mb-2" />
          <p className="text-sm">Cargando consumo…</p>
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KpiCard
              icon={CurrencyDollar} color="emerald"
              label="Gasto periodo" value={fmtUSD2(summary?.cost_usd || 0)}
              sub={fmtGTQ(summary?.cost_usd || 0)}
              data-testid="kpi-cost"
            />
            <KpiCard
              icon={Lightning} color="amber"
              label="Llamadas" value={fmtTok(summary?.calls || 0)}
              sub={`${summary?.successful || 0} exitosas`}
              data-testid="kpi-calls"
            />
            <KpiCard
              icon={Sparkle} color="purple"
              label="Tokens totales"
              value={fmtTok((summary?.input_tokens || 0) + (summary?.output_tokens || 0))}
              sub={`in ${fmtTok(summary?.input_tokens || 0)} · out ${fmtTok(summary?.output_tokens || 0)}`}
              data-testid="kpi-tokens"
            />
            <KpiCard
              icon={Calendar} color="blue"
              label="Hoy"
              value={fmtUSD2(summary?.today_cost_usd || 0)}
              sub={`${summary?.today_calls || 0} análisis · ${fmtUSD(summary?.avg_cost_per_call || 0)}/c-u`}
              data-testid="kpi-today"
            />
          </div>

          {/* Cost timeline */}
          {daily && daily.dates && daily.dates.length > 0 && (
            <CostChart daily={daily} />
          )}

          {/* Two columns: features + recent calls */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5 shadow-[var(--shadow-sm)]" data-testid="features-card">
              <div className="flex items-center gap-2 mb-3">
                <ListChecks size={18} weight="bold" className="text-[var(--primary)]" />
                <h3 className="text-sm font-black heading-font uppercase tracking-wide">Por función</h3>
              </div>
              {byFeature.length === 0 ? (
                <p className="text-xs text-[var(--text-muted)]">Sin uso en el periodo.</p>
              ) : (
                <div className="space-y-2">
                  {byFeature.map((f) => (
                    <div key={f.feature} className="flex items-center justify-between gap-2 border-b border-[var(--border)] pb-2 last:border-b-0 last:pb-0">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-[var(--text)] capitalize">{f.feature.replace('_', ' ')}</p>
                        <p className="text-[10px] text-[var(--text-muted)]">{fmtTok(f.calls)} llamadas · {fmtTok(f.tokens)} tokens</p>
                      </div>
                      <p className="text-sm font-black text-emerald-600 dark:text-emerald-400">{fmtUSD2(f.cost_usd)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5 shadow-[var(--shadow-sm)] lg:col-span-2" data-testid="recent-card">
              <div className="flex items-center gap-2 mb-3">
                <ChartLineUp size={18} weight="bold" className="text-[var(--primary)]" />
                <h3 className="text-sm font-black heading-font uppercase tracking-wide">Últimas 20 llamadas</h3>
              </div>
              {recent.length === 0 ? (
                <p className="text-xs text-[var(--text-muted)]">Sin llamadas registradas.</p>
              ) : (
                <div className="overflow-x-auto -mx-1">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wide text-[var(--text-faint)] font-bold border-b border-[var(--border)]">
                        <th className="text-left py-2 px-1">Fecha</th>
                        <th className="text-left px-1">Función</th>
                        <th className="text-right px-1">Tokens</th>
                        <th className="text-right px-1">USD</th>
                        <th className="text-right px-1 hidden md:table-cell">ms</th>
                        <th className="px-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.map((c) => (
                        <tr key={c.id} className="border-b border-[var(--border)] last:border-b-0">
                          <td className="py-1.5 px-1 text-[var(--text-muted)]">
                            {new Date(c.ts).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}
                          </td>
                          <td className="px-1 text-[var(--text)] capitalize">{c.feature.replace('_', ' ')}</td>
                          <td className="px-1 text-right font-mono">{fmtTok(c.total_tokens)}</td>
                          <td className="px-1 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">{fmtUSD(c.cost_usd)}</td>
                          <td className="px-1 text-right text-[var(--text-faint)] hidden md:table-cell">{c.duration_ms || '-'}</td>
                          <td className="px-1 text-center">
                            {c.success ? (
                              <span className="text-[10px] text-emerald-500 font-bold">✓</span>
                            ) : (
                              <span className="text-[10px] text-red-500 font-bold" title={c.error || 'error'}>✗</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const KpiCard = ({ icon: Icon, color, label, value, sub }) => {
  const map = {
    emerald: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400',
    amber: 'border-amber-500/30 bg-amber-500/5 text-amber-600 dark:text-amber-400',
    purple: 'border-purple-500/30 bg-purple-500/5 text-purple-600 dark:text-purple-400',
    blue: 'border-blue-500/30 bg-blue-500/5 text-blue-600 dark:text-blue-400',
  };
  return (
    <div className={`border rounded-[14px] p-4 ${map[color]}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={14} weight="bold" />
        <span className="text-[10px] uppercase tracking-wide font-bold opacity-80">{label}</span>
      </div>
      <p className="text-2xl font-black text-[var(--text)]">{value}</p>
      {sub && <p className="text-[10px] text-[var(--text-muted)] font-bold mt-0.5">{sub}</p>}
    </div>
  );
};

const CostChart = ({ daily }) => {
  const dates = daily.dates || [];
  const costs = daily.series?.cost_usd || [];
  const calls = daily.series?.calls || [];
  const maxCost = Math.max(0.0001, ...costs);
  const width = 100;
  const barW = width / Math.max(dates.length, 1);
  const totalCost = costs.reduce((a, b) => a + (b || 0), 0);
  const totalCalls = calls.reduce((a, b) => a + (b || 0), 0);

  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5 mb-6 shadow-[var(--shadow-sm)]" data-testid="cost-chart">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <CurrencyDollar size={18} weight="bold" className="text-emerald-500" />
        <h3 className="text-sm font-black heading-font uppercase tracking-wide">Costo diario (USD)</h3>
        <span className="ml-auto text-[10px] text-[var(--text-faint)] font-bold">
          Total: {fmtUSD2(totalCost)} · {fmtTok(totalCalls)} llamadas
        </span>
      </div>
      <div className="relative w-full h-[120px] overflow-hidden">
        <svg viewBox={`0 0 ${width} 100`} preserveAspectRatio="none" className="w-full h-full" aria-label="Costo diario">
          {dates.map((d, i) => {
            const v = costs[i] || 0;
            const h = (v / maxCost) * 92;
            const y = 96 - h;
            const pad = barW * 0.18;
            const w = Math.max(0.4, barW - pad * 2);
            return (
              <g key={d}>
                <title>{`${d}\n${calls[i]} llamadas · ${fmtUSD(v)}`}</title>
                {v > 0 ? (
                  <rect x={i * barW + pad} y={y} width={w} height={h} fill="#10B981" />
                ) : (
                  <rect x={i * barW + pad} y={95.4} width={w} height={0.6} fill="var(--border)" />
                )}
              </g>
            );
          })}
          <line x1="0" y1="96" x2={width} y2="96" stroke="var(--border)" strokeWidth="0.4" />
        </svg>
      </div>
      <div className="flex justify-between mt-2 text-[9px] text-[var(--text-faint)] font-bold">
        <span>{dates[0]}</span>
        <span>{dates[dates.length - 1]}</span>
      </div>
    </div>
  );
};

export default AiCostos;
